import React, { useEffect, useState } from 'react';

// Navbar + NavDropdown — react-bootstrap-compatible top nav ("operations control bar").
//
// Responsive (W-H1, V2-040, NAV-01): one row whenever every item fits; otherwise the Toggle
// (hamburger, on the left beside the logo) opens the menu as an overlay drawer from the left
// (W-iP15): it sits over the page rather than pushing it down, has its own close button, and
// closes when a link is followed, on Escape, or on a tap on the backdrop. Whether the row fits is
// MEASURED, not a fixed breakpoint: the menu's content width depends on the user's permissions,
// the environment badge and the badges' counts. The bar carries `data-collapsed` while collapsed;
// the drawer styling in tailwind.css keys off it.

/**
 * Fallback only, where nothing can be measured (jsdom, SSR): the width at which the full nav
 * used to fit (#810). Real browsers measure.
 */
export const NAV_DESKTOP_MIN_PX = 1360;
const DESKTOP_QUERY = `(min-width: ${NAV_DESKTOP_MIN_PX}px)`;

type DivProps = React.HTMLAttributes<HTMLDivElement>;

interface NavbarContextValue {
  /** The drawer is open (only meaningful below the `nav` breakpoint). */
  expanded: boolean;
  toggle: () => void;
  collapse: () => void;
  /** True below the `nav` breakpoint: menus render inline in the drawer instead of floating. */
  collapsedLayout: boolean;
  toggleRef: React.MutableRefObject<HTMLButtonElement | null>;
}

const NavbarCtx = React.createContext<NavbarContextValue>({
  expanded: false,
  toggle: () => {},
  collapse: () => {},
  collapsedLayout: false,
  toggleRef: { current: null },
});

/** Layout state of the enclosing Navbar - e.g. to render a submenu as a flyout or an accordion. */
export const useNavbar = () => React.useContext(NavbarCtx);

/**
 * Collapsed when the menu row does not fit. While the row is shown, an overflowing menu (content
 * wider than the room it has) collapses it and remembers the width the full row needs; while
 * collapsed, the row comes back once the bar is at least that wide. The remembered width is what
 * stops the two states flipping back and forth.
 *
 * Width-based (not input-based) - the drawer is about room on screen, not about touch.
 */
const useCollapsedLayout = (navRef: React.RefObject<HTMLElement>): boolean => {
  const fallback = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? !window.matchMedia(DESKTOP_QUERY).matches
      : false; // jsdom / SSR: assume the desktop bar
  const [collapsed, setCollapsed] = useState<boolean>(fallback);
  const neededWidthRef = React.useRef<number | null>(null);

  React.useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const canMeasure = () => nav.offsetWidth > 0 && typeof ResizeObserver !== 'undefined';

    const evaluate = () => {
      if (!canMeasure()) {
        setCollapsed(fallback());
        return;
      }
      setCollapsed((wasCollapsed) => {
        if (wasCollapsed) {
          const needed = neededWidthRef.current;
          return needed == null ? false : nav.offsetWidth < needed;
        }
        const row = nav.querySelector<HTMLElement>('.wrap-collapse');
        if (!row) return false;
        const overflow = row.scrollWidth - row.clientWidth;
        if (overflow > 1) {
          neededWidthRef.current = nav.offsetWidth + overflow + 8;
          return true;
        }
        return false;
      });
    };

    evaluate();
    if (typeof ResizeObserver === 'undefined') {
      const mql = typeof window.matchMedia === 'function' ? window.matchMedia(DESKTOP_QUERY) : null;
      mql?.addEventListener?.('change', evaluate);
      return () => mql?.removeEventListener?.('change', evaluate);
    }
    const observer = new ResizeObserver(evaluate);
    observer.observe(nav);
    const row = nav.querySelector('.wrap-collapse');
    if (row?.firstElementChild) observer.observe(row.firstElementChild);
    return () => observer.disconnect();
  }, [navRef]);

  // Showing the row again may reveal that it still does not fit (content changed meanwhile).
  React.useLayoutEffect(() => {
    const nav = navRef.current;
    if (collapsed || !nav || nav.offsetWidth === 0) return;
    const row = nav.querySelector<HTMLElement>('.wrap-collapse');
    if (row && row.scrollWidth - row.clientWidth > 1) {
      neededWidthRef.current = nav.offsetWidth + (row.scrollWidth - row.clientWidth) + 8;
      setCollapsed(true);
    }
  });

  return collapsed;
};

interface NavbarType extends React.FC<
  React.HTMLAttributes<HTMLElement> & { bg?: string; variant?: string; expand?: string; fixed?: 'top' | 'bottom' }
> {
  Brand: React.FC<DivProps>;
  Toggle: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { 'aria-controls'?: string }>;
  Collapse: React.FC<DivProps & { id?: string; title?: string }>;
}

export const Navbar = (({ bg, variant, expand, fixed, className = '', children, ...rest }) => {
  const [expanded, setExpanded] = useState(false);
  const navRef = React.useRef<HTMLElement>(null);
  const collapsedLayout = useCollapsedLayout(navRef);
  const toggleRef = React.useRef<HTMLButtonElement | null>(null);

  // Growing past the breakpoint with the drawer open would leave stale "open" state behind.
  useEffect(() => {
    if (!collapsedLayout) setExpanded(false);
  }, [collapsedLayout]);

  return (
    <NavbarCtx.Provider
      value={{
        expanded,
        toggle: () => setExpanded((e) => !e),
        collapse: () => setExpanded(false),
        collapsedLayout,
        toggleRef,
      }}
    >
      <nav
        ref={navRef}
        data-collapsed={collapsedLayout || undefined}
        className={`wrap-controlbar tw-flex tw-flex-nowrap tw-items-center tw-gap-x-1 tw-min-h-[3.25rem] tw-bg-ink tw-px-4 tw-text-white tw-shadow-md tw-border-b tw-border-ink-line ${
          fixed === 'top' ? 'tw-fixed tw-top-0 tw-left-0 tw-right-0 tw-z-[1030]' : fixed === 'bottom' ? 'tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-[1030]' : ''
        } ${className}`}
        {...rest}
      >
        {children}
      </nav>
    </NavbarCtx.Provider>
  );
}) as NavbarType;

Navbar.Brand = ({ className = '', children, ...rest }) => (
  <div className={`tw-flex tw-shrink-0 tw-items-center tw-font-display tw-text-lg tw-font-semibold tw-tracking-tight tw-text-white ${className}`} {...rest}>{children}</div>
);

Navbar.Toggle = ({ className = '', onClick, ...rest }) => {
  const { expanded, toggle, toggleRef, collapsedLayout } = React.useContext(NavbarCtx);
  if (!collapsedLayout) return null;
  return (
    <button
      ref={toggleRef}
      type="button"
      aria-label={expanded ? 'Close Menu' : 'Open Menu'}
      aria-expanded={expanded}
      onClick={(e) => { onClick?.(e); toggle(); }}
      className={`tw-mr-2 tw-inline-flex tw-min-h-[44px] tw-min-w-[44px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded tw-border tw-border-white/25 tw-text-white/90 hover:tw-bg-white/10 ${className}`}
      {...rest}
    >
      <i className={`bi ${expanded ? 'bi-x-lg' : 'bi-list'} tw-text-xl`} aria-hidden="true" />
    </button>
  );
};

Navbar.Collapse = ({ id, title = 'Menu', className = '', onClick, children, ...rest }) => {
  const { expanded, collapse, toggleRef, collapsedLayout } = React.useContext(NavbarCtx);
  const panelRef = React.useRef<HTMLDivElement>(null);

  const close = React.useCallback(() => {
    collapse();
    toggleRef.current?.focus();
  }, [collapse, toggleRef]);

  // Escape closes the drawer. Registered on `window` so it runs after a NavDropdown's document
  // listener: Escape inside an open submenu closes that submenu first (it marks the event
  // handled), and a second Escape closes the drawer.
  useEffect(() => {
    if (!expanded) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      close();
    };
    window.addEventListener('keydown', onKeyDown);
    panelRef.current?.querySelector<HTMLElement>('[data-drawer-close]')?.focus();
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [expanded, close]);

  return (
    <>
      {expanded && collapsedLayout && (
        <div
          className="wrap-nav-backdrop tw-fixed tw-inset-0 tw-z-[1040] tw-bg-ink/60"
          aria-hidden="true"
          data-testid="nav-drawer-backdrop"
          onClick={close}
        />
      )}
      <div
        ref={panelRef}
        id={id}
        data-expanded={expanded || undefined}
        // The drawer closes once a link is followed (L9) - otherwise it stays open over the page
        // that just loaded. Headers that only open a submenu are buttons, not links, so they
        // keep it open.
        onClick={(e) => {
          onClick?.(e);
          if ((e.target as Element | null)?.closest?.('a[href]')) collapse();
        }}
        className={[
          'wrap-collapse',
          collapsedLayout
            ? // Collapsed: an overlay drawer from the left (the hamburger's side), clear of the
              // notch / home indicator.
              [
                'tw-fixed tw-inset-y-0 tw-left-0 tw-z-[1050] tw-w-[85vw] tw-max-w-[22rem] tw-flex-col tw-items-stretch tw-gap-1 tw-overflow-y-auto tw-overscroll-contain tw-bg-ink tw-shadow-lg',
                'tw-pr-3 tw-pl-[max(0.75rem,env(safe-area-inset-left))] tw-pt-[max(0.5rem,env(safe-area-inset-top))] tw-pb-[max(0.75rem,env(safe-area-inset-bottom))]',
                expanded ? 'tw-flex' : 'tw-hidden',
              ].join(' ')
            : // The in-line row of the control bar. It may shrink below its content (min-w-0) and its
              // scrollWidth still reports the overflow, which is what collapses the bar. Menus float out of it.
              'tw-flex tw-min-w-0 tw-flex-1 tw-flex-row tw-items-center tw-overflow-visible',
          className,
        ].join(' ')}
        {...rest}
      >
        <div className={`wrap-drawer-header tw-items-center tw-justify-between tw-border-b tw-border-ink-line tw-pb-2 ${collapsedLayout ? 'tw-flex' : 'tw-hidden'}`}>
          <span className="tw-px-2 tw-font-display tw-text-lg tw-font-semibold tw-text-white">{title}</span>
          <button
            type="button"
            data-drawer-close
            aria-label="Close Menu"
            onClick={close}
            className="tw-inline-flex tw-min-h-[44px] tw-min-w-[44px] tw-items-center tw-justify-center tw-rounded tw-text-white/90 hover:tw-bg-white/10"
          >
            <i className="bi bi-x-lg tw-text-xl" aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </>
  );
};

/// NavDropdown — opens and closes on click or tap ONLY (L9).
//
// It used to open on hover where hover existed and ALSO toggle on click. Pointing at the menu
// opened it, so the click that followed - the click the user meant as "open" - closed it again,
// and every menu took two clicks; the first click after closing another menu was lost the same
// way. Now: one click opens; a second click on the title, a click anywhere outside, Escape, or
// choosing an item closes it. Opening one menu closes any other (the outside-press handler).
//
// `active` (W-M10) marks the title with the same red underline as a current top-level link,
// for when one of the menu's pages is open.
interface NavDropdownType extends React.FC<
  Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> & {
    title?: React.ReactNode;
    id?: string;
    /** One of this menu's pages is the current page. */
    active?: boolean;
    /** Told whenever the menu opens or closes, so a caller can reset nested state on close. */
    onOpenChange?: (open: boolean) => void;
  }
> {
  Item: React.ForwardRefExoticComponent<
    React.HTMLAttributes<HTMLElement> & {
      as?: React.ElementType;
      to?: string;
      disabled?: boolean;
      /** This item is the current page: `aria-current="page"` + the `.wrap-current-page` style. */
      current?: boolean;
    } & React.RefAttributes<HTMLElement>
  >;
  Divider: React.FC;
}

export const NavDropdown = (({ title, id, active = false, className = '', onOpenChange, children, ...rest }) => {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const { collapsedLayout: inDrawer } = React.useContext(NavbarCtx);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const onOpenChangeRef = React.useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  const mounted = React.useRef(false);

  React.useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    onOpenChangeRef.current?.(open);
  }, [open]);

  // Viewport-aware: a menu whose title sits near the right edge (Admin at 1366px) would run off
  // screen if left-aligned, so it right-aligns to its title instead. Skipped in the drawer, where
  // the menu is an in-flow accordion.
  React.useLayoutEffect(() => {
    if (!open) {
      setAlignRight(false);
      return;
    }
    const menu = menuRef.current;
    if (!menu || typeof window === 'undefined') return;
    if (window.getComputedStyle(menu).position === 'static') return;
    const rect = menu.getBoundingClientRect();
    if (rect.right > window.innerWidth - 8) setAlignRight(true);
  }, [open]);

  // Close on a press outside, and on Escape (focus returns to the title so the keyboard user
  // is not dropped at the top of the page).
  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent | MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Handled: an enclosing drawer leaves this Escape alone (see Navbar.Collapse).
        e.preventDefault();
        setOpen(false);
        rootRef.current?.querySelector<HTMLButtonElement>('button[aria-expanded]')?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={`wrap-navdropdown tw-relative ${className}`} {...rest}>
      <button
        type="button"
        id={id}
        aria-expanded={open}
        aria-haspopup="true"
        data-active={active || undefined}
        className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-md tw-py-2 tw-font-medium tw-transition hover:tw-bg-white/10 ${inDrawer ? 'tw-w-full tw-min-h-[44px] tw-px-3' : 'tw-w-auto tw-px-2.5'} ${active ? 'wrap-nav-parent-active' : ''} ${open ? 'tw-text-white tw-bg-white/10' : 'tw-text-white/90 hover:tw-text-white'}`}
        onClick={() => setOpen((o) => !o)}
      >
        {title}
        <i className={`bi bi-chevron-down tw-text-[0.7rem] tw-transition-transform tw-duration-150 ${inDrawer ? 'tw-ml-auto' : ''} ${open ? 'tw-rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        // Choosing anything inside the menu dismisses it. This is what stops the dropdown
        // hanging open over the page the user just navigated to. A control that must keep it
        // open (an Admin group header) stops the click from reaching here.
        <div
          ref={menuRef}
          className={`wrap-dropdown-menu tw-absolute tw-top-full tw-z-[1040] tw-min-w-[13rem] tw-rounded-lg tw-border tw-border-line tw-bg-surface tw-py-1.5 tw-text-content tw-shadow-lg ${alignRight ? 'tw-right-0' : 'tw-left-0'}`}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}) as NavDropdownType;

NavDropdown.Item = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement> & { as?: React.ElementType; to?: string; disabled?: boolean; current?: boolean }
>(({ as, className = '', current = false, children, ...rest }, ref) => {
  const Comp: React.ElementType = as || 'button';
  // The current item drops the default text / hover colours so `.wrap-current-page` decides
  // them - otherwise the utilities (emitted later in the stylesheet) would win.
  const look = current ? 'wrap-current-page' : 'tw-text-content hover:tw-bg-surface-hover';
  return (
    <Comp
      ref={ref}
      aria-current={current ? 'page' : undefined}
      className={`wrap-nav-item tw-block tw-w-full tw-cursor-pointer tw-px-4 tw-py-2 tw-text-left tw-text-sm tw-no-underline tw-transition ${look} ${className}`}
      {...rest}
    >
      {children}
    </Comp>
  );
});
NavDropdown.Divider = () => <div className="tw-my-1 tw-border-t tw-border-line" />;

export default Navbar;
